import { apiClient } from './client';

export interface AdminWithdrawal {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_withdrawable_cents: number;
  amount_cents: number;
  status: 'pending' | 'approved' | 'rejected' | 'done' | 'canceled' | 'partially_paid';
  user_note?: string;
  admin_note?: string;
  admin_id?: string;
  admin_email?: string;
  admin_name?: string;
  withdrawable_cents_at_request: number;
  requested_at: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  has_plaid_linked: boolean;
  bank_account_id?: string;
  bank_account_snapshot?: {
    institution_name?: string;
    mask?: string;
    account_type?: string;
    account_name?: string;
  };
  // S-L10: New fields for unified withdrawal/LOC draw display
  request_type?: 'withdrawal' | 'loc_draw';
  credit_limit_cents?: number;
  outstanding_balance_cents?: number;
  available_credit_cents?: number;
  payout_reference?: string;
  reject_reason?: string;
}

export interface AdminWithdrawalListResponse {
  withdrawals: AdminWithdrawal[];
  pagination: {
    page: number;
    per: number;
    total_count: number;
    total_pages: number;
  };
  summary: {
    pending_total_cents: number;
    approved_total_cents: number;
  };
}

export interface AdminWithdrawalFilters {
  status?: string; // comma-separated: 'pending,approved'
  from?: string; // ISO date
  to?: string; // ISO date
  user_id?: string;
  min_amount?: number; // dollars
  max_amount?: number; // dollars
  search?: string; // search user email/name
  page?: number;
  per?: number;
}

export interface ApproveWithdrawalRequest {
  admin_note?: string;
}

export interface RejectWithdrawalRequest {
  admin_note: string; // required
}

export interface MarkDoneWithdrawalRequest {
  admin_note?: string;
}

export interface AdminWithdrawalActionResponse {
  withdrawal: AdminWithdrawal;
  message: string;
}

// S-H1: Withdrawal detail with transfers
export interface AdminWithdrawalDetail extends AdminWithdrawal {
  transfers?: Array<{
    id: string;
    amount_cents: number;
    status: string;
    provider_transfer_id?: string;
    created_at: string;
    completed_at?: string;
    failure_reason?: string;
  }>;
  paid_amount_cents?: number;
  remaining_amount_cents?: number;
}

export const adminWithdrawalEndpoints = {
  // Get admin withdrawal queue
  getQueue: (filters?: AdminWithdrawalFilters) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);
    if (filters?.user_id) params.append('user_id', filters.user_id);
    if (filters?.min_amount) params.append('min_amount', filters.min_amount.toString());
    if (filters?.max_amount) params.append('max_amount', filters.max_amount.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per) params.append('per', filters.per.toString());

    const queryString = params.toString();
    return apiClient.get<AdminWithdrawalListResponse>(
      `/api/admin/withdrawals${queryString ? `?${queryString}` : ''}`
    );
  },

  // Approve a withdrawal request
  approve: (id: string, data: ApproveWithdrawalRequest) => {
    return apiClient.post<AdminWithdrawalActionResponse>(
      `/api/admin/withdrawals/${id}/approve`,
      data
    );
  },

  // Approve and initiate payout in single atomic transaction
  approveAndPayout: (id: string, data: ApproveWithdrawalRequest) => {
    return apiClient.post<AdminWithdrawalActionResponse>(
      `/api/admin/withdrawal_requests/${id}/approve_and_payout`,
      data
    );
  },

  // Reject a withdrawal request
  reject: (id: string, data: RejectWithdrawalRequest) => {
    return apiClient.post<AdminWithdrawalActionResponse>(
      `/api/admin/withdrawals/${id}/reject`,
      data
    );
  },

  // Mark a withdrawal as done
  markDone: (id: string, data: MarkDoneWithdrawalRequest) => {
    return apiClient.post<AdminWithdrawalActionResponse>(
      `/api/admin/withdrawals/${id}/mark_done`,
      data
    );
  },

  // Initiate payout via Plaid (automatic)
  initiatePayout: (id: string, amount_cents?: number) => {
    return apiClient.post<AdminWithdrawalActionResponse>(
      `/api/admin/withdrawal_requests/${id}/initiate_payout`,
      amount_cents ? { amount_cents } : {}
    );
  },

  // S-H1: Get withdrawal detail with transfers
  getDetail: (id: string) => {
    return apiClient.get<AdminWithdrawalDetail>(`/api/admin/withdrawals/${id}`);
  },
};
